/**
 * Arbeidstidsoversikt's rows (plan 0026 P2, step 2): one per person, and what in their
 * registrations needs a manager. Pure, so the page only draws.
 *
 * Mode-aware (owner, 2026-09-21): with Vaktplan a finished shift is judged by Timeliste's kinds
 * (close.ts), so a planned shift nobody clocked is "Mangler stempling". Without it there is no
 * plan to miss, and only objective faults count: an open registration, an end before its start,
 * two registrations over each other. An empty day is never a fault, and hours over the agreed
 * week are not one either.
 */
import type { ApiShift, ApiUser } from '@/api/data';
import { oslo, worked } from '@/rules';
import type { BreakPolicy } from '@timefly/core';
import { onDuty } from '../ansatt/summary';
import { kindOf } from '../timeliste/close';

export type Issue = 'open' | 'invalid' | 'overlap' | 'missing' | 'deviation';
export const ISSUE_TEXT: Record<Issue, string> = {
  open: 'Åpen registrering',
  invalid: 'Ugyldig tid',
  overlap: 'Overlappende registreringer',
  missing: 'Mangler stempling',
  deviation: 'Avvik fra plan',
};
/** Order on a row: what blocks pay first. */
const ORDER: Issue[] = ['open', 'invalid', 'overlap', 'missing', 'deviation'];

const at = (iso: string | null | undefined) => (iso ? Date.parse(iso) : Number.NaN);
/** Past this a clock-in with no clock-out is forgotten, not somebody at work (ansatt/summary onDuty). */
const FORGOTTEN_MS = 16 * 3600_000;

/** The one thing wrong with a shift, or null. `scheduling` is Innstillinger → Modus. */
export function issueOf(s: ApiShift, now: number, scheduling: boolean, tol = 15): Issue | null {
  if (s.is_approved) return null;
  const pin = at(s.punch_in_time);
  const pout = at(s.punch_out_time);
  if (s.punch_in_time && s.punch_out_time && pout <= pin) return 'invalid';
  if (!scheduling) {
    if (s.punch_in_time && !s.punch_out_time && now - pin > FORGOTTEN_MS) return 'open';
    return null;
  }
  const k = kindOf(s, now, tol);
  return k === 'open' || k === 'missing' || k === 'deviation' ? k : null;
}

/** Shift ids of one person's registrations that lie over another of theirs (both punches set). */
export function overlapping(mine: ApiShift[]): Set<number> {
  const done = mine
    .filter((s) => s.punch_in_time && s.punch_out_time && at(s.punch_out_time) > at(s.punch_in_time))
    .sort((a, b) => at(a.punch_in_time) - at(b.punch_in_time));
  const out = new Set<number>();
  for (let i = 1; i < done.length; i++) {
    const a = done[i - 1] as ApiShift;
    const b = done[i] as ApiShift;
    if (at(b.punch_in_time) < at(a.punch_out_time)) {
      out.add(a.id);
      out.add(b.id);
    }
  }
  return out;
}

export interface Row {
  u: ApiUser;
  /** The shift this person is clocked into now, null when not at work. */
  duty: ApiShift | null;
  /** Hours today and in the period, after break; a running shift counts its time so far. */
  today: number;
  period: number;
  /** One entry per shift that needs a manager, worst first. */
  issues: { issue: Issue; shift: ApiShift }[];
}

const hoursOf = (s: ApiShift, duty: ApiShift | null, now: number, policy: BreakPolicy | null) =>
  s.id === duty?.id ? Math.max(0, (now - at(s.punch_in_time)) / 3600_000) : (worked(s, policy) ?? 0);

export function rowsFor(
  users: ApiUser[],
  shifts: ApiShift[],
  opts: {
    from: string;
    to: string;
    today: string;
    now: number;
    scheduling: boolean;
    policy: BreakPolicy | null;
    tol?: number;
  },
): Row[] {
  const { from, to, today, now, scheduling, policy, tol } = opts;
  const by = new Map<number, ApiShift[]>();
  for (const s of shifts) if (s.user_id != null) by.set(s.user_id, [...(by.get(s.user_id) ?? []), s]);
  return users.map((u) => {
    const mine = by.get(u.id) ?? [];
    const duty = onDuty(mine, now);
    const dateOf = (s: ApiShift) => oslo(s.start_time)?.date ?? '';
    const inPeriod = mine.filter((s) => dateOf(s) >= from && dateOf(s) <= to);
    const over = overlapping(inPeriod);
    const issues = inPeriod
      .map((shift) => ({
        shift,
        issue: issueOf(shift, now, scheduling, tol) ?? (over.has(shift.id) ? 'overlap' : null),
      }))
      .filter((x): x is { shift: ApiShift; issue: Issue } => x.issue != null)
      .sort((a, b) => ORDER.indexOf(a.issue) - ORDER.indexOf(b.issue));
    const sum = (ss: ApiShift[]) => ss.reduce((a, s) => a + hoursOf(s, duty, now, policy), 0);
    return { u, duty, today: sum(mine.filter((s) => dateOf(s) === today)), period: sum(inPeriod), issues };
  });
}

/** "6:12" for 6.2 hours; the brief's clock format, so a column of them lines up. */
export const hm = (h: number) => {
  const m = Math.round(h * 60);
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
};
